import { ApiBaseService, ApiV2Service } from './api.base.service';
import store from '@/store/store';
import {DeviceGatewayTask, SuccessResponse, Task} from '../types';
import Transformation from '../classes/transformation';

export interface SearchResponse {
  total: number;
  columnFilters?: any;
}

export type SearchTasksResponse = { tasks: Task[] } & SearchResponse;
export type SearchDeviceGatewayTasksResponse = { tasks: DeviceGatewayTask[] } & SearchResponse;

export class TaskService {
  private api: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
  }

  public async getTasks(
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false, filters: Record<string, Record<string, boolean>> = {},
    searchNames: string | null = null,
    projectId: number | null = null,
    dueBefore: string | Date | null = null,
    completedAfter: string | Date | null = null,
  ): Promise<SearchTasksResponse> {
    const organisation = store.getters.activeOrganisation[0].id;

    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);

    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (projectId) {
      filtersString += `&projectId=${projectId}`;
    }
    if (searchNames) {
      filtersString += `&searchNames=${searchNames}`;
    }
    if (dueBefore) {
      if (dueBefore instanceof Date) {
        dueBefore = dueBefore.toISOString();
      }
      filtersString += `&dueBefore=${dueBefore}`;
    }
    if (completedAfter) {
      if (completedAfter instanceof Date) {
        completedAfter = completedAfter.toISOString();
      }
      filtersString += `&completedAfter=${completedAfter}`;
    }
    return this.api.get(`organisation/${organisation}/tasks?` +
      `take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }


  public async getDeviceGatewayTasks(
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false, filters: Record<string, Record<string, boolean>> = {},
    searchNames: string | null = null,
    projectId: number | null = null,
    dueBefore: string | Date | null = null,
    completedAfter: string | Date | null = null,
  ): Promise<SearchDeviceGatewayTasksResponse> {
    const organisation = store.getters.activeOrganisation[0].id;

    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);
    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (projectId) {
      filtersString += `&projectId=${projectId}`;
    }
    if (searchNames) {
      filtersString += `&searchNames=${searchNames}`;
    }
    if (dueBefore) {
      if (dueBefore instanceof Date) {
        dueBefore = dueBefore.toISOString();
      }
      filtersString += `&dueBefore=${dueBefore}`;
    }
    if (completedAfter) {
      if (completedAfter instanceof Date) {
        completedAfter = completedAfter.toISOString();
      }
      filtersString += `&completedAfter=${completedAfter}`;
    }
    return this.api.get(`organisation/${organisation}/deviceGatewayTasks?` +
      `take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }

  public async getTask(id: number): Promise<Task> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisation}/tasks/${id}`);
  }

  public async getTaskByPositionId(positionId: number): Promise<SearchTasksResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisation}/positions/${positionId}/tasks`);
  }

  public async getMyTasks(organisationId?: number): Promise<Task[]> {
    return this.api.get(`user/tasks?organisationId=${organisationId || ''}`);
  }

  public async createTask(details: object): Promise<Task> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/tasks`, details);
  }

  public async updateTask(id: number, payload: object): Promise<Task> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/task/${id}`, payload);
  }

  public async changeTaskDueDate(id: number, date: Date): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/task/${id}/changeDueDate`, { date });
  }

  public async changeTasksDueDate(ids: number[], date: Date): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/task/changeDueDate`, { tasks: ids, date });
  }

  public async completeTask(id: number): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/task/${id}/complete`, null);
  }

  public async completeTasks(ids: number[]): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/task/complete`, { tasks: ids });
  }

  public async assignTask(id: number, userId: number): Promise<Task> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/task/${id}/assign`, { userId });
  }

  public async assignTasks(ids: number[], userId: number): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/task/assign`, { tasks: ids, userId });
  }

  public async setNote(id: number, taskNote: string): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/task/${id}/setNotes`, { task_note: taskNote });
  }
}

export default new TaskService();
